import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Form, Spinner } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import { GoogleLogin } from "@react-oauth/google";

import { useAuth } from "@/_zustand/auth";
import {
  useLoginUsuario,
  useLoginGoogle,
  useLoginApple,
} from "@/hooks/useAuth";
import { getPostLoginPath } from "@/hooks/useCompanyFlow";
import { getEnvVariables } from "@/constants/getEnvVariables";
import { guardarSesion } from "@/lib/sesionTokens";
import { ModalCredential } from "./ModalCredential";
import AppleLogo from "@/assets/imgs/Apple_Logo.png";
import Logo from "@/assets/imgs/logo.png";
import "./login.css";

const APPLE_SDK_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const APPLE_USER_CACHE_KEY = "appleUserName";

export const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { APPLE_SERVICES_ID } = getEnvVariables();
  const { setSesionAuth } = useAuth();
  const sendLogin = useLoginUsuario();
  const sendLoginGoogle = useLoginGoogle();
  const sendLoginApple = useLoginApple();
  const [show, setShow] = useState(false);
  const [user, setUser] = useState({});

  useEffect(() => {
    if (!document.querySelector(`script[src="${APPLE_SDK_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = APPLE_SDK_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // CA-20: si el auto-login encadenado de SignIn falla, el usuario llega acá
  // con el correo precargado (state.correo) para no tener que retipearlo.
  const defaultValues = {
    usuario_correo: location.state?.correo || "",
    usuario_pass: "",
    usuario_device: "WEB",
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({ defaultValues });

  const getFormErrorMessage = (name) =>
    errors[name] && (
      <small className="text-danger">{errors[name].message}</small>
    );

  // Handler compartido por Google y Apple (CA-19): guarda los tokens de
  // sesión emitidos por el backend, y solo si el usuario ya tiene rut/nombre/
  // apellido completos marca la sesión como `authed`. Si falta algún dato,
  // se abre ModalCredential con la sesión ya guardada pero SIN marcarla
  // `authed` todavía (el usuario puede cerrar el modal y seguir navegando/
  // pagando con tarjeta, con sesión válida y sin claim `rut`, caso borde 22).
  const establecerSesionSocial = (resp) => {
    if (!resp || resp.status === "error") {
      toast.error(resp?.message || "No se pudo iniciar sesión.");
      return;
    }

    guardarSesion({ accessToken: resp.accessToken, refreshToken: resp.refreshToken });

    if (
      !resp.user ||
      !resp.user.usuario_rut ||
      !resp.user.usuario_nombre ||
      !resp.user.usuario_apellido
    ) {
      setUser(resp.user || {});
      setShow(true);
      return;
    }

    toast.success("Sesión iniciada correctamente");
    setSesionAuth(resp.user);
    navigate(getPostLoginPath());
  };

  // CA-15/CA-16: password en texto plano contra loginUsuario (bcrypt server-side,
  // ya no client-side); CA-15bis: usuario_device "WEB", igual que el registro.
  const onSubmit = (formData) => {
    if (sendLogin.isLoading) return;

    sendLogin
      .mutateAsync(formData)
      .then((resp) => {
        if (resp.status === "error") {
          toast.error(resp.message || "Usuario o contraseña incorrectos");
          return;
        }

        guardarSesion({ accessToken: resp.accessToken, refreshToken: resp.refreshToken });
        toast.success("Sesión iniciada correctamente");
        setSesionAuth(resp.user);
        navigate(getPostLoginPath());
      })
      .catch(() => {
        toast.error("Usuario o contraseña incorrectos");
      });
  };

  // CA-17: GoogleLogin entrega credentialResponse.credential = id_token
  // directamente — ya no hace falta el flujo implícito de useGoogleLogin ni
  // la llamada a userinfo/v2/me.
  const handleGoogleSuccess = (credentialResponse) => {
    const idToken = credentialResponse?.credential;

    if (!idToken) {
      toast.error("No se pudo iniciar sesión con Google. Inténtalo nuevamente.");
      return;
    }

    sendLoginGoogle
      .mutateAsync({ id_token: idToken })
      .then(establecerSesionSocial)
      .catch(() => {
        toast.error("No se pudo iniciar sesión con Google. Inténtalo nuevamente.");
      });
  };

  // CA-17bis: GoogleLogin no expone un equivalente de onNonOAuthError (popup
  // bloqueado/cerrado sin pasar por onError) — se compensa con el texto fijo
  // bajo el botón y con que el formulario de contraseña nunca se deshabilita
  // por el estado de este flujo.
  const handleGoogleError = () => {
    toast.error("No se pudo iniciar sesión con Google. Inténtalo nuevamente.");
  };

  const handleAppleLogin = async () => {
    if (!window.AppleID) {
      toast.error(
        "No se pudo cargar Apple Sign In. Verifica tu conexión e inténtalo de nuevo.",
      );
      return;
    }

    try {
      window.AppleID.auth.init({
        clientId: APPLE_SERVICES_ID,
        scope: "name email",
        redirectURI: `${window.location.origin}/apple-callback`,
        usePopup: true,
      });

      const appleResp = await window.AppleID.auth.signIn();
      const idToken = appleResp?.authorization?.id_token;

      if (!idToken) {
        toast.error("No se pudo iniciar sesión con Apple. Inténtalo nuevamente.");
        return;
      }

      // Apple solo envía el nombre la primera vez que el usuario autoriza la
      // app; en logins siguientes hay que reusar el que guardamos localmente.
      let usuario_nombre = appleResp?.user?.name?.firstName;
      let usuario_apellido = appleResp?.user?.name?.lastName;

      if (usuario_nombre || usuario_apellido) {
        localStorage.setItem(
          APPLE_USER_CACHE_KEY,
          JSON.stringify({ usuario_nombre, usuario_apellido }),
        );
      } else {
        try {
          const cached = JSON.parse(
            localStorage.getItem(APPLE_USER_CACHE_KEY) || "null",
          );
          usuario_nombre = cached?.usuario_nombre;
          usuario_apellido = cached?.usuario_apellido;
        } catch {
          // Cache corrupta o inexistente: se completará vía ModalCredential.
        }
      }

      sendLoginApple
        .mutateAsync({ id_token: idToken, usuario_nombre, usuario_apellido })
        .then(establecerSesionSocial)
        .catch(() => {
          toast.error("Error al iniciar sesión con Apple.");
        });
    } catch (error) {
      // El SDK de Apple rechaza la promesa (sin onError dedicado) cuando el
      // usuario cierra el popup o lo cancela — no mostramos error en ese caso.
      if (error?.error === "popup_closed_by_user") return;
      console.error("Login con Apple fallido:", error);
      toast.error("No se pudo iniciar sesión con Apple. Inténtalo nuevamente.");
    }
  };

  return (
    <div className="login-container">
      <ModalCredential show={show} setShow={setShow} user={user} />
      <div className="login-left-image" />
      <div className="login-form-container">
        <div>
          <img src={Logo} alt="Openmatic Logo" className="login-logo" />
          <h2 className="login-welcome">¡Te damos la bienvenida!</h2>
          <p className="login-subtitle">Inicia sesión</p>

          <div className="login-btn login-google-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
            />
          </div>
          {/* CA-17bis: compensa la pérdida de onNonOAuthError (popup bloqueado
              o cerrado sin pasar por onError) — el usuario siempre tiene una
              salida visible hacia el login por correo. */}
          <p className="login-google-hint">
            ¿No ves el botón de Google o no se abre? Inicia sesión con tu
            correo más abajo.
          </p>

          <button
            className="login-btn login-apple"
            type="button"
            onClick={handleAppleLogin}
          >
            <img src={AppleLogo} alt="Apple" />
            Inicia con Apple
          </button>

          <p className="login-or">O continúa con Email</p>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="login-input-group">
              <Controller
                name="usuario_correo"
                control={control}
                rules={{
                  required: "Debe indicar el correo",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                    message: "Correo no válido",
                  },
                }}
                render={({ field, fieldState }) => (
                  <Form.Group className="position-relative mb-2 ">
                    <Form.Label className="mb-2">
                      Correo <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Email"
                      id={field.name}
                      {...field}
                      isInvalid={fieldState.invalid}
                    />
                    {getFormErrorMessage("usuario_correo")}
                  </Form.Group>
                )}
              />
            </div>

            <div className="login-input-group">
              <Controller
                name="usuario_pass"
                control={control}
                rules={{
                  required: "Debe indicar el correo",
                  minLength: { value: 6, message: "Mínimo 6 caracteres" },
                }}
                render={({ field, fieldState }) => (
                  <Form.Group className="position-relative mb-2 ">
                    <Form.Label className="mb-2">
                      Contraseña <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Contraseña"
                      id={field.name}
                      {...field}
                      isInvalid={fieldState.invalid}
                    />
                    {getFormErrorMessage("usuario_pass")}
                  </Form.Group>
                )}
              />
            </div>

            <Button
              type="submit"
              className="btn mb-3 m-auto verde text-white"
              style={{ width: "100%", borderColor: "transparent" }}
              disabled={sendLogin.isLoading}
            >
              {sendLogin.isLoading ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                    role="status"
                    aria-hidden="true"
                  ></Spinner>
                  Iniciando Sesión...
                </>
              ) : (
                "Iniciar Sesión"
              )}
            </Button>
          </Form>

          <p className="login-forgot">
            <Link to="/forgot-password">Olvidé mi contraseña</Link>
          </p>
          <p className="login-register">
            ¿No tienes cuenta?
            <Link to="/register"> Regístrate aquí</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
